/* ==========================================================================
   Joy loyalty — points balance in the navigation
   Theme: Pippadu & Ceramide 2026

   The markup is already correct on first paint (snippets/joy-nav-points.liquid
   renders it from the customer metafield). This file only does the two things
   Liquid cannot:

     1. Click → open the Joy widget instead of navigating to the loyalty page.
     2. Refresh the number from the SDK, because the metafield is a snapshot
        that can lag points earned earlier in the same session.

   Both degrade to plain navigation. That matters here: the Joy widget is
   currently scoped to selected pages in the Joy admin, so on most pages
   `window.joyInstance` and the widget trigger simply are not there. When the
   merchant later enables the widget site-wide, the click upgrades itself with
   no code change.
   ========================================================================== */

(function () {
  'use strict';

  var LINK_SELECTOR = '[data-joy-open-widget]';
  var VALUE_SELECTOR = '[data-joy-points-value]';

  /* openWidget() calls window.avadaJoyTrigger(), which is published by the
     widget's own mount. SDK present but trigger missing = clicking would
     silently do nothing, so treat that as "not available". */
  function widgetReady() {
    return !!(
      window.joyInstance &&
      typeof window.joyInstance.openWidget === 'function' &&
      typeof window.avadaJoyTrigger === 'function'
    );
  }

  /* The widget's bundle lands long after the page is usable. Measured on this
     shop, from navigation start:

       DOMContentLoaded   2.6s   <- the nav entry is on screen and clickable
       window.joyInstance 5.7s
       window.avadaJoyTrigger / the FAB container   8.4s

     So for about six seconds every page has a loyalty link that cannot open a
     widget yet. Falling through to the href there — which is what this used to
     do — spends the click on a page load, and since the greeting sits at the
     left of the row it is the half that gets clicked first, which is why it
     looked like the greeting behaved differently from the points pill. It did
     not; it was just clicked earlier.

     An early click is held instead and spends its wait on what was actually
     asked for. The href stays as the floor: if the widget never arrives, the
     click still lands on the loyalty page rather than doing nothing. */
  var WIDGET_WAIT_MS = 8000;
  var POLL_MS = 60;

  function triggerWidget() {
    try {
      window.joyInstance.openWidget();
      return true;
    } catch (e) {
      return false;
    }
  }

  function settle(link) {
    link.removeAttribute('data-joy-waiting');
    link.removeAttribute('aria-busy');
  }

  function onClick(event) {
    if (event.defaultPrevented) return;
    /* Let people open the loyalty page in a new tab as usual. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    /* Captured now: by the time the poll below runs the event is spent and
       currentTarget has been cleared. */
    var link = event.currentTarget;
    event.preventDefault();

    if (widgetReady()) {
      if (!triggerWidget()) window.location.href = link.href;
      return;
    }

    /* Already waiting on an earlier click. Swallow this one rather than queue a
       second open: on widget V4 the trigger is a toggle, so the pair would open
       the widget and immediately shut it again. */
    if (link.hasAttribute('data-joy-waiting')) return;

    link.setAttribute('data-joy-waiting', '');
    link.setAttribute('aria-busy', 'true');

    var waited = 0;
    var timer = window.setInterval(function () {
      waited += POLL_MS;

      if (widgetReady()) {
        window.clearInterval(timer);
        settle(link);
        if (!triggerWidget()) window.location.href = link.href;
        return;
      }

      if (waited >= WIDGET_WAIT_MS) {
        window.clearInterval(timer);
        settle(link);
        window.location.href = link.href;
      }
    }, POLL_MS);
  }

  function groupThousands(value) {
    var digits = String(Math.max(0, Math.round(Number(value) || 0)));
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  function paint(points) {
    var nodes = document.querySelectorAll(VALUE_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      var template = nodes[i].getAttribute('data-joy-points-template') || '{points}';
      var next = template.replace(/\{points\}/g, groupThousands(points));
      if (nodes[i].textContent !== next) nodes[i].textContent = next;
    }
  }

  function refreshFromSdk() {
    if (!window.joyInstance || typeof window.joyInstance.customer !== 'function') return;

    var result;
    try {
      result = window.joyInstance.customer();
    } catch (e) {
      return;
    }
    if (!result || typeof result.then !== 'function') return;

    result
      .then(function (customer) {
        /* The SDK resolves rather than throws on failure, so check the shape. */
        if (customer && typeof customer.point === 'number') paint(customer.point);
      })
      .catch(function () {});
  }

  /* ------------------------------------------------------------------
     Hiding the floating launcher

     No hard-coded selector: the Joy bundle is minified, so its class names
     are not a contract to build on. Instead identify the launcher by what it
     provably is — a small, fixed-position, Joy-owned element parked in a
     viewport corner. The panel is fixed too but far larger, so the size cap
     separates them without needing to know either class name.

     Note for the team: the supported switch for this lives in the Joy admin
     (widget display settings). This is the theme-side belt and braces.

     Why releaseHosts() exists
     ------------------------
     Widget V4 (AVADA_JOY.settings.version === 4) changed the shape of this.
     The launcher's container is now #Avada-Joy_FloatingButton at ~118x48 —
     inside the size cap, so it is what gets matched — and on first open the
     widget REPLACES the launcher button inside that same container rather
     than mounting elsewhere. `visibility` is inherited, so the class we put
     on the container was riding down onto the panel: the widget opened at
     full size and was simply invisible, which reads as "the click does
     nothing". Measured on the live shop, open with the class on:

         joy-loyalty-widget  1400x813  visibility: hidden   <- opened, unseen
         (same call, class off)        visibility: visible

     So an element only stays hidden while it holds nothing bigger than a
     launcher. The moment it hosts the panel it is no longer the launcher and
     the class comes off. Nothing reappears when it does: V4 has already
     swapped the button out for the panel by then.
     ------------------------------------------------------------------ */
  var LAUNCHER_MAX = 140;

  /* Bigger than a launcher could ever be, therefore the panel. Direct children
     only: the widget mounts as one, and walking the open panel's whole subtree
     on every mutation would not be free. */
  function hostsPanel(el) {
    var kids = el.children;
    for (var i = 0; i < kids.length; i++) {
      var box = kids[i].getBoundingClientRect();
      if (box.width > LAUNCHER_MAX || box.height > LAUNCHER_MAX) return true;
    }
    return false;
  }

  function releaseHosts() {
    var hidden = document.querySelectorAll('.joy-launcher-hidden');
    for (var i = 0; i < hidden.length; i++) {
      if (hostsPanel(hidden[i])) hidden[i].classList.remove('joy-launcher-hidden');
    }
  }

  function hideLauncher() {
    if (!document.documentElement.classList.contains('joy-hide-launcher')) return;

    var candidates = document.querySelectorAll(
      '[id*="joy" i],[class*="joy" i],[id*="avada" i],[class*="avada" i]'
    );

    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.classList.contains('joy-launcher-hidden')) continue;
      /* Never touch our own navigation entry. */
      if (el.closest('.joy-nav-points')) continue;

      var style = window.getComputedStyle(el);
      if (style.position !== 'fixed') continue;

      var box = el.getBoundingClientRect();
      if (!box.width || !box.height) continue;
      if (box.width > LAUNCHER_MAX || box.height > LAUNCHER_MAX) continue;
      if (hostsPanel(el)) continue;

      el.classList.add('joy-launcher-hidden');
    }
  }

  function watchLauncher() {
    hideLauncher();

    /* The widget mounts late and can re-render, so keep watching rather than
       checking once. Cheap: the work is gated on the html class.

       Releasing runs before the debounce, not inside it: the panel appearing
       under a hidden container is exactly the mutation being reported, and a
       120ms wait there is 120ms of the shopper looking at nothing. */
    var observer = new MutationObserver(function () {
      releaseHosts();
      window.clearTimeout(watchLauncher._t);
      watchLauncher._t = window.setTimeout(hideLauncher, 120);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function boot() {
    var links = document.querySelectorAll(LINK_SELECTOR);

    for (var i = 0; i < links.length; i++) {
      links[i].addEventListener('click', onClick);
    }

    if (window.joyInstance) refreshFromSdk();
    document.addEventListener('joy:ready', refreshFromSdk);

    if (document.documentElement.classList.contains('joy-hide-launcher')) watchLauncher();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
